/**
 * Link Line
 * 
 * Animates the line within a link on hover and focus events.
 * 
 * @returns {void} Initializes the link line animation functionality.
 */
// Libraries
import { gsap } from 'gsap';

export default function initLinkLine() {
    document.querySelectorAll('a.z-link-fat').forEach(link => {
        const lines = Array.from(link.querySelectorAll('svg polyline, svg path, svg line'));
        if (!lines.length) return;

        link.classList.add('has-squiggle');

        const dashed = new Set(lines.filter(l => {
            const d = getComputedStyle(l).strokeDasharray;
            return d && d !== 'none';
        }));
        const lengths = new Map(lines.map(l => [l, l.getTotalLength()]));
        // Dashed lines keep their own dasharray, so they're revealed by growing x2 / moving x1 instead.
        const ends = new Map([...dashed].map(l => [l, parseFloat(l.getAttribute('x2'))]));

        const reset = l => {
            if (dashed.has(l)) {
                gsap.set(l, { attr: { x1: 0, x2: 0 }, strokeDashoffset: 0 });
            } else {
                gsap.set(l, { strokeDasharray: lengths.get(l), strokeDashoffset: lengths.get(l) });
            }
        };
        lines.forEach(reset);

        let outTween = null;
        let line = lines[0];
        let length = lengths.get(line);

        const drawIn = () => {
            // Pick a different random line each time the link is activated.
            const candidates = lines.length > 1 ? lines.filter(l => l !== line) : lines;
            gsap.killTweensOf(lines);
            if (outTween) outTween.kill();
            lines.forEach(reset);
            line = candidates[Math.floor(Math.random() * candidates.length)];
            length = lengths.get(line);
            if (dashed.has(line)) {
                gsap.to(line, { attr: { x2: ends.get(line) }, duration: 0.6, ease: 'power2.out' });
            } else {
                gsap.fromTo(line,
                    { strokeDashoffset: length },
                    { strokeDashoffset: 0, duration: 0.6, ease: 'power2.out' });
            }
        };
        const drawOut = () => {
            gsap.killTweensOf(line);
            if (outTween) outTween.kill();
            if (dashed.has(line)) {
                // Offset tracks x1 so the dash pattern stays anchored while the start moves.
                const end = ends.get(line);
                gsap.set(line, { attr: { x2: end }, strokeDashoffset: 0 });
                // Single tween writes x1 and dashoffset together so they never drift a frame apart.
                const state = { x: parseFloat(line.getAttribute('x1')) || 0 };
                outTween = gsap.to(state, {
                    x: end,
                    duration: 0.6,
                    ease: 'power2.in',
                    onUpdate: () => {
                        line.setAttribute('x1', state.x);
                        line.style.strokeDashoffset = state.x;
                    }
                });
            } else {
                gsap.to(line, { strokeDashoffset: -length, duration: 0.6, ease: 'power2.in' });
            }
        };

        link.addEventListener('mouseenter', drawIn);
        link.addEventListener('mouseleave', drawOut);
        link.addEventListener('focus', drawIn);
        link.addEventListener('blur', drawOut);
    });
};